import { api } from '../api/client'
import { config } from '../config'
import type { BulkMediaAccount, CategoryOverview, JobDetail, JobSummary, NewJob, NewStagedFile } from './types'

/** What the Bulk Media page and the Vault Upload button call; tests pass fakes. */
export interface BulkMediaApi {
  accounts: () => Promise<BulkMediaAccount[]>
  categories: (accountIds: string[]) => Promise<CategoryOverview>
  createFile: (body: { filename: string; content_type: string; size: number }) => Promise<NewStagedFile>
  /** One chunk of a staged file; `onProgress` gets the bytes of this chunk sent so far. */
  putChunk: (fileId: string, index: number, data: Blob, onProgress: (sent: number) => void, signal?: AbortSignal) => Promise<void>
  completeFile: (fileId: string) => Promise<{ etag: string }>
  discardFile: (fileId: string) => Promise<unknown>
  createJob: (body: NewJob) => Promise<JobDetail>
  jobs: () => Promise<JobSummary[]>
  job: (jobId: string) => Promise<JobDetail>
  cancelJob: (jobId: string) => Promise<JobDetail>
  retryJob: (jobId: string) => Promise<JobDetail>
}

function errorOf(xhr: XMLHttpRequest): Error {
  try {
    const detail = (JSON.parse(xhr.responseText) as { detail?: unknown }).detail
    if (typeof detail === 'string' && detail) return new Error(detail)
  } catch {
    // not JSON (Cloudflare page, proxy error)
  }
  return new Error(xhr.status ? `HTTP error ${xhr.status}` : 'Network error')
}

/** XHR, not fetch: fetch reports no upload progress, and a chunk is 32 MiB. */
function sendChunk(url: string, data: Blob, token: string | null, onProgress: (sent: number) => void, signal?: AbortSignal) {
  return new Promise<number>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    xhr.upload.onprogress = (e) => onProgress(e.loaded)
    xhr.onload = () => (xhr.status === 401 || (xhr.status >= 200 && xhr.status < 300) ? resolve(xhr.status) : reject(errorOf(xhr)))
    xhr.onerror = () => reject(errorOf(xhr))
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'))
    signal?.addEventListener('abort', () => xhr.abort(), { once: true })
    xhr.send(data)
  })
}

async function putChunk(fileId: string, index: number, data: Blob, onProgress: (sent: number) => void, signal?: AbortSignal) {
  const url = `${config.apiUrl}/bulk-media/files/${fileId}/chunks/${index}`
  let status = await sendChunk(url, data, api.getToken() ?? localStorage.getItem('access_token'), onProgress, signal)
  if (status === 401) {
    const fresh = await api.refreshAccessToken()
    if (!fresh) throw new Error('Your session ended. Log in again.')
    status = await sendChunk(url, data, fresh, onProgress, signal)
    if (status === 401) throw new Error('Your session ended. Log in again.')
  }
}

export const bulkMediaApi: BulkMediaApi = {
  accounts: () => api.getBulkMediaAccounts(),
  categories: (ids) => api.getBulkMediaCategories(ids),
  createFile: (body) => api.createBulkMediaFile(body),
  putChunk,
  completeFile: (id) => api.completeBulkMediaFile(id),
  discardFile: (id) => api.discardBulkMediaFile(id),
  createJob: (body) => api.createBulkMediaJob(body),
  jobs: () => api.getBulkMediaJobs(),
  job: (id) => api.getBulkMediaJob(id),
  cancelJob: (id) => api.cancelBulkMediaJob(id),
  retryJob: (id) => api.retryBulkMediaJob(id),
}
